import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Package, AlertTriangle, Calendar, Tag, Upload, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { useFeatures } from '../context/FeatureContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default product types - will be replaced by admin-configured types
const DEFAULT_PRODUCT_TYPES = [
  { id: 'nuc', label: 'Nuc', icon: '📦' },
  { id: 'queen', label: 'Queen', icon: '👑' },
  { id: 'bee_package', label: 'Bee Package', icon: '🐝' },
  { id: 'hive', label: 'Hive', icon: '🏠' },
  { id: 'equipment', label: 'Equipment', icon: '🔧' },
  { id: 'wax', label: 'Wax', icon: '🕯️' },
  { id: 'propolis', label: 'Propolis', icon: '🧪' },
  { id: 'pollen', label: 'Pollen', icon: '🌼' },
  { id: 'honey', label: 'Honey', icon: '🍯' },
  { id: 'other', label: 'Other', icon: '📋' },
];

const SEASONS = [
  { id: 'all_year', label: 'All Year' },
  { id: 'spring', label: 'Spring Only' },
  { id: 'summer', label: 'Summer Only' },
  { id: 'spring_summer', label: 'Spring & Summer' },
  { id: 'fall', label: 'Fall Only' },
];

const ProductsView = () => {
  const { freePlatform } = useFeatures();
  const [products, setProducts] = useState([]);
  const [productTypes, setProductTypes] = useState(DEFAULT_PRODUCT_TYPES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publicListingEnabled, setPublicListingEnabled] = useState(null);
  const [userPlan, setUserPlan] = useState('free');
  
  const [formData, setFormData] = useState({
    name: '',
    product_type: 'nuc',
    description: '',
    stock_quantity: 0,
    seasonal_availability: 'all_year',
    is_active: true,
    price: '',
    unit: '',
    image_url: ''
  });
  const [uploading, setUploading] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchProducts();
    fetchProductTypes();
    fetchPublicListingStatus();
    fetchUserPlan();
  }, []);

  const fetchUserPlan = async () => {
    try {
      if (!token) return;
      const res = await fetch(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setUserPlan(data.subscription_plan || 'free');
      }
    } catch {}
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_URL}/api/products`, { headers });
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products || []);
      }
    } catch (err) {
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchProductTypes = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_URL}/api/admin/config/product_types`, { headers });
      if (res.ok) {
        const data = await res.json();
        if (data.items && data.items.length > 0) {
          // Map admin-configured types to the format we need
          const configuredTypes = data.items
            .filter(item => item.is_active)
            .map(item => ({
              id: item.id || item.name.toLowerCase().replace(/\s+/g, '_'),
              label: item.name,
              icon: item.icon || '📦'
            }));
          if (configuredTypes.length > 0) {
            setProductTypes(configuredTypes);
          }
        }
      }
    } catch (err) {
    }
  };

  const fetchPublicListingStatus = async () => {
    try {
      if (!token) return;
      const res = await fetch(`${API_URL}/api/profile/public-listing`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPublicListingEnabled(data.enabled || false);
      }
    } catch (err) {}
  };

  const enablePublicListing = async () => {
    try {
      await fetch(`${API_URL}/api/auth/me/visibility`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ public_listing_enabled: true })
      });
      setPublicListingEnabled(true);
      toast.success('Public listing enabled! Your products will appear on Find Local Honey.');
    } catch (err) {
      toast.error('Failed to enable public listing');
    }
  };


  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Product name is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      };

      const url = editingProduct 
        ? `${API_URL}/api/products/${editingProduct.id}`
        : `${API_URL}/api/products`;
      
      const method = editingProduct ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        fetchProducts();
        setShowAddDialog(false);
        setEditingProduct(null);
        resetForm();
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to save product');
      }
    } catch (err) {
      setError('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await fetch(`${API_URL}/api/products/${productId}`, { method: 'DELETE', headers });
      fetchProducts();
    } catch (err) {
      setError('Failed to delete product');
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      product_type: product.product_type || 'nuc',
      description: product.description || '',
      stock_quantity: product.stock_quantity || 0,
      seasonal_availability: product.seasonal_availability || 'all_year',
      is_active: product.is_active !== false,
      price: product.price || '',
      unit: product.unit || '',
      image_url: product.image_url || ''
    });
    setShowAddDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      product_type: ['free', 'trial'].includes(userPlan) ? 'honey' : 'nuc',
      description: '',
      stock_quantity: 0,
      seasonal_availability: 'all_year',
      is_active: true,
      price: '',
      unit: '',
      image_url: ''
    });
  };

  const openAddDialog = () => {
    setEditingProduct(null);
    resetForm();
    setShowAddDialog(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/products/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const data = await res.json();
      setFormData(prev => ({ ...prev, image_url: `${API_URL}/api/files/${data.path}` }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Group products by type
  const productsByType = productTypes.reduce((acc, type) => {
    acc[type.id] = products.filter(p => p.product_type === type.id);
    return acc;
  }, {});

  // Also include products with types not in the current list
  const uncategorizedProducts = products.filter(p => 
    !productTypes.find(t => t.id === p.product_type)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-stone-500">Loading products...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
            <Package className="h-6 w-6 text-amber-600" />
            My Products
          </h2>
          <p className="text-stone-500 mt-1">Manage your products for sale</p>
        </div>
        <Button onClick={openAddDialog} className="bg-amber-500 hover:bg-amber-600">
          <Plus className="h-4 w-4 mr-2" />
          Add Product
        </Button>
      </div>

      {/* Public Listing Prompt */}
      {publicListingEnabled === false && products.length > 0 && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <Package className="h-5 w-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-stone-800">Want customers to find your products?</h3>
            <p className="text-sm text-stone-600 mt-0.5">
              Enable your public listing so your products appear on the <strong>"Find Local Honey"</strong> page where local buyers can discover you.
            </p>
            <Button onClick={enablePublicListing} className="mt-2 bg-amber-600 hover:bg-amber-700 text-white text-sm h-9 px-4">
              Enable Public Listing
            </Button>
          </div>
        </div>
      )}

      {publicListingEnabled && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center gap-2 text-sm text-green-700">
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          Your products are visible on <button onClick={() => window.location.href='/?view=find-honey'} className="font-semibold underline hover:text-green-800 cursor-pointer bg-transparent border-none p-0 text-green-700 text-sm">Find Local Honey</button>
        </div>
      )}

      {/* Free Plan Restriction Info */}
      {!freePlatform && ['free', 'trial'].includes(userPlan) && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-start gap-3" data-testid="free-plan-info">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-sm">
            &#x1f36f;
          </div>
          <div>
            <p className="text-sm text-blue-800 font-medium">Honey Products Only on Your Plan</p>
            <p className="text-xs text-blue-600 mt-0.5">
              On the Backyard Beekeeper plan, only your <strong>Honey</strong> products are displayed on Find Local Honey. 
              Upgrade your plan to list Nucs, Queens, Equipment, and more.
            </p>
            <a href="/subscription" className="inline-block mt-1.5 text-xs font-semibold text-blue-700 hover:text-blue-900 underline">
              View Plans & Upgrade
            </a>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          {error}
          <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">×</button>
        </div>
      )}

      {/* Products Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {productTypes.slice(0, 5).map(type => {
          const count = productsByType[type.id]?.length || 0;
          return (
            <Card key={type.id} className="bg-stone-50">
              <CardContent className="p-4 text-center">
                <div className="text-2xl mb-1">{type.icon}</div>
                <div className="text-xl font-bold text-stone-700">{count}</div>
                <div className="text-xs text-stone-500">{type.label}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Products by Type */}
      {productTypes.map(type => (
        <Card key={type.id} className="border-stone-200">
          <CardHeader className="py-3 bg-stone-50">
            <CardTitle className="text-base flex items-center gap-2">
              <span className="text-xl">{type.icon}</span>
              {type.label}
              <span className="text-xs bg-stone-200 text-stone-600 px-2 py-0.5 rounded-full ml-2">
                {productsByType[type.id]?.length || 0}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3">
            {!productsByType[type.id] || productsByType[type.id].length === 0 ? (
              <p className="text-sm text-stone-400 text-center py-4">
                No {type.label.toLowerCase()} products configured
              </p>
            ) : (
              <div className="space-y-2">
                {productsByType[type.id].map(product => (
                  <div 
                    key={product.id} 
                    className="flex items-center justify-between p-3 bg-white border border-stone-100 rounded-lg hover:border-stone-200 transition-colors gap-3"
                  >
                    {/* Thumbnail */}
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name} className="w-14 h-14 rounded-lg object-cover flex-shrink-0 border border-stone-200" />
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
                        <Package className="h-6 w-6 text-stone-300" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-stone-800 flex items-center gap-2 flex-wrap">
                        {product.name}
                        {product.price && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold">
                            ${product.price}{product.unit ? `/${product.unit}` : ''}
                          </span>
                        )}
                        {!product.is_active && (
                          <span className="text-xs bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded">
                            Inactive
                          </span>
                        )}
                        {product.stock_quantity > 0 && (
                          <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                            <Tag className="w-3 h-3" />
                            {product.stock_quantity} in stock
                          </span>
                        )}
                        {product.seasonal_availability !== 'all_year' && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {SEASONS.find(s => s.id === product.seasonal_availability)?.label}
                          </span>
                        )}
                      </div>
                      {product.description && (
                        <p className="text-sm text-stone-500 mt-1 truncate">{product.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => openEditDialog(product)}
                        className="text-stone-600 hover:text-amber-600"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDelete(product.id)} 
                        className="text-stone-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      {/* Uncategorized Products */}
      {uncategorizedProducts.length > 0 && (
        <Card className="border-stone-200">
          <CardHeader className="py-3 bg-stone-50">
            <CardTitle className="text-base flex items-center gap-2">
              <span className="text-xl">📋</span>
              Other
              <span className="text-xs bg-stone-200 text-stone-600 px-2 py-0.5 rounded-full ml-2">
                {uncategorizedProducts.length}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3">
            <div className="space-y-2">
              {uncategorizedProducts.map(product => (
                <div 
                  key={product.id} 
                  className="flex items-center justify-between p-3 bg-white border border-stone-100 rounded-lg"
                >
                  <div className="flex-1">
                    <div className="font-medium text-stone-800">{product.name}</div>
                    {product.description && (
                      <p className="text-sm text-stone-500 mt-1">{product.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEditDialog(product)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(product.id)} className="text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Product Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Product Type */}
            <div>
              <Label>Product Type</Label>
              {['free', 'trial'].includes(userPlan) && (
                <p className="text-xs text-amber-600 mt-1 font-medium">Free plan: Honey products only. Upgrade to list other product types.</p>
              )}
              <div className="grid grid-cols-5 gap-2 mt-2">
                {productTypes.slice(0, 10).map(type => {
                  const isLocked = ['free', 'trial'].includes(userPlan) && type.id !== 'honey';
                  return (
                    <button
                      key={type.id}
                      onClick={() => !isLocked && setFormData({ ...formData, product_type: type.id })}
                      disabled={isLocked}
                      className={`p-2 rounded-lg border-2 text-center transition-all ${
                        isLocked
                          ? 'border-stone-100 bg-stone-50 opacity-40 cursor-not-allowed'
                          : formData.product_type === type.id 
                            ? 'border-amber-500 bg-amber-50' 
                            : 'border-stone-200 hover:border-stone-300'
                      }`}
                    >
                      <div className="text-xl">{type.icon}</div>
                      <div className="text-xs mt-1 truncate">{type.label}</div>
                      {isLocked && <div className="text-[10px] text-stone-400">Upgrade</div>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Product Name */}
            <div>
              <Label>Product Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Italian Nuc, Carniolan Queen"
                className="mt-1"
              />
            </div>

            {/* Description */}
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
                className="mt-1"
                rows={2}
              />
            </div>

            {/* Product Image */}
            <div>
              <Label>Product Image</Label>
              <div className="mt-1">
                {formData.image_url ? (
                  <div className="relative inline-block">
                    <img src={formData.image_url} alt="Product" className="w-24 h-24 rounded-xl object-cover border border-stone-200" />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image_url: '' })}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                    >
                      <XIcon className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 p-3 border-2 border-dashed border-stone-300 rounded-xl cursor-pointer hover:border-amber-400 hover:bg-amber-50/30 transition-colors">
                    <Upload className="h-5 w-5 text-stone-400" />
                    <span className="text-sm text-stone-500">{uploading ? 'Uploading...' : 'Upload product photo'}</span>
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                  </label>
                )}
              </div>
            </div>

            {/* Price & Unit */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="e.g., 15.00"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Unit</Label>
                <select
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  className="mt-1 w-full h-10 px-3 border border-stone-200 rounded-md text-sm"
                >
                  <option value="">No unit</option>
                  <option value="jar">per jar</option>
                  <option value="kg">per kg</option>
                  <option value="lb">per lb</option>
                  <option value="bottle">per bottle</option>
                  <option value="pack">per pack</option>
                  <option value="each">each</option>
                  <option value="tub">per tub</option>
                </select>
              </div>
            </div>

            {/* Stock & Availability */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Stock Quantity</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.stock_quantity}
                  onChange={(e) => setFormData({ ...formData, stock_quantity: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Availability</Label>
                <select
                  value={formData.seasonal_availability}
                  onChange={(e) => setFormData({ ...formData, seasonal_availability: e.target.value })}
                  className="mt-1 w-full h-10 px-3 border border-stone-200 rounded-md text-sm"
                >
                  {SEASONS.map(s => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
              <div>
                <Label className="text-sm font-medium">Active (available for sale)</Label>
                <p className="text-xs text-stone-500">Inactive products will not appear in sales</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-600">
                {saving ? 'Saving...' : editingProduct ? 'Update' : 'Add Product'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsView;
